<template>
  <div class="app-container">
    <div v-if="!hasPermission" class="no-permission">
      <i class="el-icon-warning-outline"></i>
      <h2>您没有权限访问此页面</h2>
      <p>请联系系统管理员获取权限</p>
    </div>

    <template v-else>
      <!-- 搜索区域 -->
      <el-card class="search-card" shadow="never">
        <el-form
          :model="filters"
          ref="queryForm"
          size="small"
          :inline="true"
          label-width="80px"
          @submit.native.prevent
        >
          <el-form-item label="产品名称" prop="productName">
            <el-input
              v-model="filters.productName"
              placeholder="请输入产品名称"
              clearable
              style="width: 240px;"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" size="mini" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" size="mini" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 列表区域 -->
      <el-card class="table-card" shadow="never">
        <div class="table-toolbar">
          <div class="toolbar-left">
            <el-button
              type="primary"
              plain
              icon="el-icon-plus"
              size="mini"
              @click="openCreateDialog"
            >新增</el-button>
          </div>
          <div class="toolbar-right">
            <el-tooltip content="刷新" placement="top">
              <el-button
                circle
                icon="el-icon-refresh"
                size="mini"
                @click="fetchList"
              />
            </el-tooltip>
          </div>
        </div>

        <el-table
          v-loading="loading"
          :data="pagedList"
          border
          stripe
          size="small"
          style="width: 100%;"
          :header-cell-style="headerCellStyle"
        >
          <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod" />
          <el-table-column prop="id" label="产品编号" width="90" align="center" />
          <el-table-column prop="productName" label="产品名称" min-width="160" show-overflow-tooltip />
          <el-table-column label="单价" width="130" align="center">
            <template slot-scope="scope">
              <span class="price-text">¥{{ formatPrice(scope.row.price) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="产品描述" min-width="200" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.description || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdTime" label="创建时间" width="170" align="center">
            <template slot-scope="scope">
              {{ formatTime(scope.row.createdTime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="center" fixed="right" class-name="small-padding fixed-width">
            <template slot-scope="scope">
              <el-button
                type="text"
                icon="el-icon-edit"
                size="mini"
                @click="openEditDialog(scope.row)"
              >修改</el-button>
              <el-button
                type="text"
                icon="el-icon-delete"
                size="mini"
                class="danger-text"
                @click="handleDelete(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredList.length"
            :current-page.sync="pageNum"
            :page-size.sync="pageSize"
            :page-sizes="[10, 20, 50]"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </el-card>

      <!-- 新增/修改弹窗 -->
      <el-dialog
        :title="dialogMode === 'create' ? '添加产品' : '修改产品'"
        :visible.sync="dialogVisible"
        width="520px"
        append-to-body
        :close-on-click-modal="false"
        @closed="resetForm"
      >
        <el-form
          :model="form"
          :rules="rules"
          ref="productForm"
          label-width="90px"
          size="small"
        >
          <el-form-item label="产品名称" prop="productName">
            <el-input v-model="form.productName" placeholder="请输入产品名称" maxlength="200" />
          </el-form-item>
          <el-form-item label="单价" prop="price">
            <el-input-number
              v-model="form.price"
              :min="0"
              :precision="2"
              :step="100"
              controls-position="right"
              style="width: 100%;"
            />
          </el-form-item>
          <el-form-item label="产品描述" prop="description">
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="3"
              placeholder="请输入内容"
              maxlength="500"
              show-word-limit
            />
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button type="primary" size="small" :loading="submitting" @click="handleSubmit">确 定</el-button>
          <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        </div>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import request from '@/utils/request'

export default {
  name: 'ProductManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      productList: [],
      filters: { productName: '' },
      pageNum: 1,
      pageSize: 10,
      dialogVisible: false,
      dialogMode: 'create',
      form: {
        id: null,
        productName: '',
        price: 0,
        description: ''
      },
      rules: {
        productName: [
          { required: true, message: '产品名称不能为空', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '单价不能为空', trigger: 'blur' }
        ]
      },
      headerCellStyle: {
        background: '#f8f8f9',
        color: '#515a6e',
        fontWeight: '600'
      }
    }
  },
  computed: {
    ...mapGetters({
      permission: 'user/permission'
    }),
    hasPermission() {
      try {
        const localStoragePermission = localStorage.getItem('user-permission')
        return localStoragePermission === '1' || this.permission === '1' || this.permission === 1
      } catch (error) {
        return false
      }
    },
    filteredList() {
      const keyword = (this.filters.productName || '').trim().toLowerCase()
      if (!keyword) return this.productList
      return this.productList.filter(item =>
        (item.productName || '').toLowerCase().includes(keyword)
      )
    },
    pagedList() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.filteredList.slice(start, start + this.pageSize)
    }
  },
  created() {
    if (this.hasPermission) {
      this.fetchList()
    }
  },
  methods: {
    indexMethod(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    formatPrice(price) {
      const num = Number(price)
      return Number.isFinite(num) ? num.toFixed(2) : '0.00'
    },
    formatTime(time) {
      if (!time) return '—'
      return String(time).replace('T', ' ').substring(0, 19)
    },
    async fetchList() {
      this.loading = true
      try {
        const data = await request.get('/api/product/list')
        this.productList = Array.isArray(data) ? data : []
      } catch (error) {
        this.$message.error((error && error.message) || '获取产品列表失败')
        this.productList = []
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pageNum = 1
    },
    handleReset() {
      this.filters.productName = ''
      this.pageNum = 1
      this.fetchList()
    },
    handleSizeChange() {
      this.pageNum = 1
    },
    handlePageChange() {},
    openCreateDialog() {
      this.dialogMode = 'create'
      this.resetForm()
      this.dialogVisible = true
    },
    openEditDialog(row) {
      this.dialogMode = 'edit'
      this.form = {
        id: row.id,
        productName: row.productName || '',
        price: Number(row.price) || 0,
        description: row.description || ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.clearValidate()
      })
    },
    resetForm() {
      this.form = {
        id: null,
        productName: '',
        price: 0,
        description: ''
      }
      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.clearValidate()
      })
    },
    handleSubmit() {
      this.$refs.productForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            productName: this.form.productName.trim(),
            price: this.form.price,
            description: (this.form.description || '').trim()
          }
          if (this.dialogMode === 'create') {
            await request.post('/api/product', payload)
            this.$message.success('新增成功')
          } else {
            payload.id = this.form.id
            await request.put('/api/product', payload)
            this.$message.success('修改成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (error) {
          this.$message.error((error && error.message) || '操作失败，请重试')
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`是否确认删除产品编号为"${row.id}"的数据项？`, '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request.delete(`/api/product/${row.id}`)
          this.$message.success('删除成功')
          if (this.pagedList.length === 1 && this.pageNum > 1) {
            this.pageNum -= 1
          }
          this.fetchList()
        } catch (error) {
          this.$message.error((error && error.message) || '删除失败，请重试')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
}

.no-permission {
  background: #fff;
  border-radius: 4px;
  padding: 60px 24px;
  text-align: center;
  color: #909399;
}

.no-permission i {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 12px;
}

.no-permission h2 {
  margin: 8px 0;
  font-size: 18px;
  color: #303133;
  font-weight: 500;
}

.search-card {
  margin-bottom: 10px;
}

.search-card >>> .el-card__body {
  padding: 18px 18px 0;
}

.table-card >>> .el-card__body {
  padding: 16px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-text {
  color: #f56c6c;
  font-weight: 600;
}

.danger-text {
  color: #f56c6c !important;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  text-align: right;
}
</style>
